import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Shield, Globe, AlertTriangle, CheckCircle, Clock, Key, FileText } from 'lucide-react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import toast from 'react-hot-toast';

interface CertificateInfo {
  domain: string;
  issuer: string;
  subject: string;
  validFrom: string;
  validTo: string;
  daysUntilExpiry: number;
  isValid: boolean;
  isExpired: boolean;
  serialNumber: string;
  signatureAlgorithm: string;
  keySize: string;
  fingerprint: string;
  subjectAltNames: string[];
  keyUsage: string[];
  extendedKeyUsage: string[];
  isWildcard: boolean;
  isSelfSigned: boolean;
  chainLength: number;
  protocol: string;
  cipherSuite: string;
}

export const SslCertificateAnalyzerTool: React.FC = () => {
  const { t } = useTranslation();
  const [domain, setDomain] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [certificateInfo, setCertificateInfo] = useState<CertificateInfo | null>(null);
  const [error, setError] = useState('');

  // Since we can't directly access SSL certificates from the browser due to CORS,
  // we'll simulate the analysis with a mock implementation
  const analyzeCertificate = async (hostname: string): Promise<CertificateInfo> => {
    // This is a mock implementation since browser security prevents direct SSL certificate access
    // In a real implementation, this would need to be done through a backend service
    
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    // Mock certificate data for demonstration
    const mockCertificates: { [key: string]: Partial<CertificateInfo> } = {
      'google.com': {
        issuer: 'GTS CA 1C3',
        subject: 'CN=*.google.com',
        validFrom: '2024-01-15T08:30:00Z',
        validTo: '2024-04-08T08:29:59Z',
        serialNumber: '0x1234567890abcdef',
        signatureAlgorithm: 'SHA256-RSA',
        keySize: '2048 bits',
        subjectAltNames: ['*.google.com', 'google.com'],
        isWildcard: true,
        isSelfSigned: false,
        chainLength: 3,
        protocol: 'TLS 1.3',
        cipherSuite: 'TLS_AES_256_GCM_SHA384'
      },
      'github.com': {
        issuer: 'DigiCert TLS Hybrid ECC SHA384 2020 CA1',
        subject: 'CN=github.com',
        validFrom: '2024-02-01T00:00:00Z',
        validTo: '2025-02-01T23:59:59Z',
        serialNumber: '0xabcdef1234567890',
        signatureAlgorithm: 'ECDSA-SHA384',
        keySize: '256 bits (ECC)',
        subjectAltNames: ['github.com', 'www.github.com'],
        isWildcard: false,
        isSelfSigned: false,
        chainLength: 2,
        protocol: 'TLS 1.3',
        cipherSuite: 'TLS_AES_128_GCM_SHA256'
      }
    };

    const mockData = mockCertificates[hostname] || {
      issuer: 'Let\'s Encrypt Authority X3',
      subject: `CN=${hostname}`,
      validFrom: '2024-01-01T00:00:00Z',
      validTo: '2024-12-31T23:59:59Z',
      serialNumber: '0x' + Math.random().toString(16).substr(2, 16),
      signatureAlgorithm: 'SHA256-RSA',
      keySize: '2048 bits',
      subjectAltNames: [hostname],
      isWildcard: hostname.startsWith('*.'),
      isSelfSigned: false,
      chainLength: 2,
      protocol: 'TLS 1.2',
      cipherSuite: 'ECDHE-RSA-AES256-GCM-SHA384'
    };

    const validFrom = new Date(mockData.validFrom!);
    const validTo = new Date(mockData.validTo!);
    const now = new Date();
    const daysUntilExpiry = Math.ceil((validTo.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    const isExpired = now > validTo;
    const isValid = now >= validFrom && now <= validTo;

    return {
      domain: hostname,
      issuer: mockData.issuer!,
      subject: mockData.subject!,
      validFrom: validFrom.toISOString(),
      validTo: validTo.toISOString(),
      daysUntilExpiry,
      isValid,
      isExpired,
      serialNumber: mockData.serialNumber!,
      signatureAlgorithm: mockData.signatureAlgorithm!,
      keySize: mockData.keySize!,
      fingerprint: 'SHA256:' + Array.from({length: 32}, () => Math.floor(Math.random() * 16).toString(16)).join('').toUpperCase(),
      subjectAltNames: mockData.subjectAltNames!,
      keyUsage: ['Digital Signature', 'Key Encipherment'],
      extendedKeyUsage: ['Server Authentication', 'Client Authentication'],
      isWildcard: mockData.isWildcard!,
      isSelfSigned: mockData.isSelfSigned!,
      chainLength: mockData.chainLength!,
      protocol: mockData.protocol!,
      cipherSuite: mockData.cipherSuite!
    };
  };

  const handleAnalyze = async () => {
    if (!domain.trim()) {
      toast.error('Please enter a domain name');
      return;
    }

    // Clean domain input
    let cleanDomain = domain.trim().toLowerCase();
    cleanDomain = cleanDomain.replace(/^https?:\/\//, '');
    cleanDomain = cleanDomain.replace(/\/.*$/, '');
    cleanDomain = cleanDomain.split(':')[0];

    setIsAnalyzing(true);
    setError('');
    setCertificateInfo(null);

    try {
      const info = await analyzeCertificate(cleanDomain);
      setCertificateInfo(info);
      toast.success('Certificate analyzed successfully');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to analyze certificate');
      toast.error('Failed to analyze certificate');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const getExpiryStatus = (daysUntilExpiry: number, isExpired: boolean) => {
    if (isExpired) {
      return { color: 'text-red-600 dark:text-red-400', icon: AlertTriangle, text: 'Expired' };
    }
    if (daysUntilExpiry <= 7) {
      return { color: 'text-red-600 dark:text-red-400', icon: AlertTriangle, text: 'Expires Soon' };
    }
    if (daysUntilExpiry <= 30) {
      return { color: 'text-yellow-600 dark:text-yellow-400', icon: AlertTriangle, text: 'Expires Soon' };
    }
    return { color: 'text-green-600 dark:text-green-400', icon: CheckCircle, text: 'Valid' };
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: 'short'
    });
  };

  const commonDomains = ['google.com', 'github.com', 'stackoverflow.com', 'mozilla.org', 'cloudflare.com'];

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Shield className="w-5 h-5" />
            <span>SSL Certificate Analyzer</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex space-x-2">
              <Input
                label="Domain Name"
                value={domain}
                onChange={(e) => setDomain(e.target.value)}
                placeholder="example.com"
                className="flex-1"
              />
              <div className="flex items-end">
                <Button
                  onClick={handleAnalyze}
                  disabled={!domain.trim() || isAnalyzing}
                  variant="primary"
                >
                  {isAnalyzing ? 'Analyzing...' : 'Analyze'}
                </Button>
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              {commonDomains.map((commonDomain) => (
                <Button
                  key={commonDomain}
                  onClick={() => setDomain(commonDomain)}
                  variant="outline"
                  size="sm"
                >
                  {commonDomain}
                </Button>
              ))}
            </div>

            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-md text-red-700 dark:bg-red-900/20 dark:border-red-800 dark:text-red-400">
                <div className="flex items-center space-x-2">
                  <AlertTriangle className="w-4 h-4" />
                  <span>{error}</span>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Certificate Information */}
      {certificateInfo && (
        <>
          {/* Status Overview */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Globe className="w-5 h-5" />
                <span>Certificate Status</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="text-center p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <div className="text-lg font-bold text-gray-900 dark:text-gray-100">
                    {certificateInfo.domain}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Domain</div>
                </div>
                
                <div className="text-center p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                  <div className={`text-lg font-bold ${getExpiryStatus(certificateInfo.daysUntilExpiry, certificateInfo.isExpired).color}`}>
                    {getExpiryStatus(certificateInfo.daysUntilExpiry, certificateInfo.isExpired).text}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Status</div>
                </div>
                
                <div className="text-center p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                  <div className="text-lg font-bold text-green-600 dark:text-green-400">
                    {certificateInfo.daysUntilExpiry > 0 ? certificateInfo.daysUntilExpiry : 0}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Days Until Expiry</div>
                </div>
                
                <div className="text-center p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                  <div className="text-lg font-bold text-purple-600 dark:text-purple-400">
                    {certificateInfo.protocol}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Protocol</div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Certificate Details */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Basic Information */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <FileText className="w-5 h-5" />
                  <span>Certificate Details</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Subject:</span>
                    <span className="font-mono text-right">{certificateInfo.subject}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Issuer:</span>
                    <span className="font-mono text-right">{certificateInfo.issuer}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Serial Number:</span>
                    <span className="font-mono text-right">{certificateInfo.serialNumber}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Signature Algorithm:</span>
                    <span className="font-mono text-right">{certificateInfo.signatureAlgorithm}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Key Size:</span>
                    <span className="font-mono text-right">{certificateInfo.keySize}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Chain Length:</span>
                    <span className="font-mono text-right">{certificateInfo.chainLength}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Validity Period */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Clock className="w-5 h-5" />
                  <span>Validity Period</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 text-sm">
                  <div>
                    <span className="text-gray-600 dark:text-gray-400">Valid From:</span>
                    <div className="font-mono mt-1">{formatDate(certificateInfo.validFrom)}</div>
                  </div>
                  <div>
                    <span className="text-gray-600 dark:text-gray-400">Valid To:</span>
                    <div className="font-mono mt-1">{formatDate(certificateInfo.validTo)}</div>
                  </div>
                  <div>
                    <span className="text-gray-600 dark:text-gray-400">Fingerprint (SHA256):</span>
                    <div className="font-mono text-xs mt-1 break-all">{certificateInfo.fingerprint}</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Subject Alternative Names */}
          <Card>
            <CardHeader>
              <CardTitle>Subject Alternative Names (SAN)</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {certificateInfo.subjectAltNames.map((san, index) => (
                  <span
                    key={index}
                    className="px-2 py-1 bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400 rounded text-sm font-mono"
                  >
                    {san}
                  </span>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Security Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Key className="w-5 h-5" />
                <span>Security Information</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">Key Usage:</h4>
                  <ul className="space-y-1 text-sm text-gray-600 dark:text-gray-400">
                    {certificateInfo.keyUsage.map((usage, index) => (
                      <li key={index}>• {usage}</li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">Extended Key Usage:</h4>
                  <ul className="space-y-1 text-sm text-gray-600 dark:text-gray-400">
                    {certificateInfo.extendedKeyUsage.map((usage, index) => (
                      <li key={index}>• {usage}</li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <div className={`font-bold ${certificateInfo.isWildcard ? 'text-green-600 dark:text-green-400' : 'text-gray-600 dark:text-gray-400'}`}>
                    {certificateInfo.isWildcard ? 'Yes' : 'No'}
                  </div>
                  <div className="text-xs text-gray-500">Wildcard</div>
                </div>
                
                <div className="text-center p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <div className={`font-bold ${certificateInfo.isSelfSigned ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
                    {certificateInfo.isSelfSigned ? 'Yes' : 'No'}
                  </div>
                  <div className="text-xs text-gray-500">Self-Signed</div>
                </div>
                
                <div className="text-center p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <div className="font-bold text-blue-600 dark:text-blue-400">
                    {certificateInfo.protocol}
                  </div>
                  <div className="text-xs text-gray-500">TLS Version</div>
                </div>
                
                <div className="text-center p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <div className="font-bold text-purple-600 dark:text-purple-400 text-xs">
                    {certificateInfo.cipherSuite.split('_')[0]}
                  </div>
                  <div className="text-xs text-gray-500">Cipher</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {/* Disclaimer */}
      <Card>
        <CardHeader>
          <CardTitle>Important Notice</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg dark:bg-yellow-900/20 dark:border-yellow-800">
            <div className="flex items-start space-x-2">
              <AlertTriangle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 mt-0.5" />
              <div className="text-sm text-yellow-800 dark:text-yellow-200">
                <p className="font-medium mb-1">Demo Mode</p>
                <p>
                  This tool currently shows simulated certificate data for demonstration purposes. 
                  In a production environment, this would connect to a backend service to fetch real SSL certificate information 
                  due to browser security restrictions (CORS policy).
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Usage Guide */}
      <Card>
        <CardHeader>
          <CardTitle>Usage Guide</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
            <div>
              <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">Features:</h4>
              <ul className="space-y-1 text-gray-600 dark:text-gray-400">
                <li>• Certificate validity checking</li>
                <li>• Expiration date monitoring</li>
                <li>• Subject Alternative Names (SAN) analysis</li>
                <li>• Key usage and security information</li>
                <li>• TLS protocol and cipher suite details</li>
                <li>• Certificate chain analysis</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">Use Cases:</h4>
              <ul className="space-y-1 text-gray-600 dark:text-gray-400">
                <li>• SSL certificate monitoring</li>
                <li>• Security compliance checking</li>
                <li>• Certificate renewal planning</li>
                <li>• Domain security assessment</li>
                <li>• TLS configuration verification</li>
                <li>• Certificate troubleshooting</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
